/**
 * Routes PWA Surveillant — sous `/surveillant/*`.
 *
 * Garde : utilisateur doit avoir un rôle surveillance.
 * Injecte aussi le manifest PWA dédié pour installation.
 */
import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';

import Accueil from './pages/Accueil';
import AppelWrapper from './pages/AppelWrapper';
import JustificationsWrapper from './pages/JustificationsWrapper';
import EdtWrapper from './pages/EdtWrapper';
import ConduiteWrapper from './pages/ConduiteWrapper';
import AbsencesWrapper from './pages/AbsencesWrapper';
import Profil from './pages/Profil';

const ROLES_SURVEILLANT = ['surveillant', 'admin', 'super_admin', 'agent'];

const useManifestSurveillant = () => {
  useEffect(() => {
    document.querySelectorAll('link[rel="manifest"]').forEach((el) => {
      if ((el as HTMLLinkElement).id !== 'surveillant-manifest') {
        el.parentNode?.removeChild(el);
      }
    });
    if (document.getElementById('surveillant-manifest')) return;
    const link = document.createElement('link');
    link.rel = 'manifest';
    link.href = '/surveillant-manifest.json';
    link.id = 'surveillant-manifest';
    document.head.appendChild(link);
    return () => {
      try { document.head.removeChild(link); } catch { /* ignore */ }
    };
  }, []);
};

const SurveillantGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useManifestSurveillant();
  const user = useSelector((s: RootState) => s.auth.user);
  const token = useSelector((s: RootState) => s.auth.token);

  if (!token) return <Navigate to="/login" replace />;
  if (!user) return null;  // attendre le chargement de l'utilisateur
  if (!ROLES_SURVEILLANT.includes(user.role || '')) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
};

export default function SurveillantRoutes() {
  return (
    <SurveillantGuard>
      <Routes>
        <Route index element={<Navigate to="accueil" replace />} />
        <Route path="accueil" element={<Accueil />} />
        <Route path="appel" element={<AppelWrapper />} />
        <Route path="justifications" element={<JustificationsWrapper />} />
        <Route path="edt" element={<EdtWrapper />} />
        <Route path="conduite" element={<ConduiteWrapper />} />
        <Route path="absences" element={<AbsencesWrapper />} />
        <Route path="profil" element={<Profil />} />
        {/* Fix 2026-09-02 : chemin absolu (voir portail/formateur pour raison) */}
        <Route path="*" element={<Navigate to="/surveillant/accueil" replace />} />
      </Routes>
    </SurveillantGuard>
  );
}
